import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { LineChart } from "@mui/x-charts/LineChart";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import GoogleIcon from "@mui/icons-material/Google";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import AppShell from "../components/AppShell";
import ExportButton from "../components/ExportButton";
import ApiService from "../services/ApiService";
import IntegrationsService from "../services/IntegrationsService";
import ReportService from "../services/ReportService";

// ── metric definitions ────────────────────────────────────────────────────────

const META_ADS_KINDS = ["facebook_ads_account"];

const METRIC_GROUPS = [
  {
    label: "Meta Ads (paid)",
    providers: ["meta"],
    kinds: META_ADS_KINDS,
    color: "#1877F2",
    metrics: [
      { key: "spend", label: "Spend" },
      { key: "impressions", label: "Impressions" },
      { key: "clicks", label: "Clicks" },
      { key: "ctr", label: "CTR (%)" },
      { key: "cpc", label: "CPC" },
      { key: "conversions", label: "Conversions" },
      { key: "revenue", label: "Revenue" },
      { key: "roas", label: "ROAS" },
    ],
  },
  {
    label: "Facebook / Instagram (organic)",
    providers: ["meta"],
    excludeKinds: META_ADS_KINDS,
    color: "#E1306C",
    metrics: [
      { key: "reach", label: "Reach" },
      { key: "impressions", label: "Impressions" },
      { key: "engagements", label: "Engagements" },
      { key: "followers", label: "Followers" },
      { key: "profileViews", label: "Profile views (IG)" },
    ],
  },
  {
    label: "Google Analytics 4",
    providers: ["google"],
    color: "#EA4335",
    metrics: [
      { key: "sessions", label: "Sessions" },
      { key: "users", label: "Users" },
      { key: "newUsers", label: "New users" },
      { key: "pageViews", label: "Page views" },
      { key: "conversions", label: "Conversions" },
    ],
  },
  {
    label: "Google Search Console",
    providers: ["search_console"],
    color: "#34A853",
    metrics: [
      { key: "clicks", label: "Clicks" },
      { key: "impressions", label: "Impressions" },
      { key: "ctr", label: "CTR (%)" },
      { key: "position", label: "Avg. position" },
    ],
  },
  {
    label: "Google Ads",
    providers: ["google_ads"],
    color: "#FBBC04",
    metrics: [
      { key: "impressions", label: "Impressions" },
      { key: "clicks", label: "Clicks" },
      { key: "cost", label: "Spend" },
      { key: "conversions", label: "Conversions" },
      { key: "ctr", label: "CTR (%)" },
      { key: "cpc", label: "CPC" },
    ],
  },
  {
    label: "LinkedIn Ads",
    providers: ["linkedin"],
    color: "#0A66C2",
    metrics: [
      { key: "impressions", label: "Impressions" },
      { key: "clicks", label: "Clicks" },
      { key: "spend", label: "Spend" },
      { key: "conversions", label: "Conversions" },
      { key: "leads", label: "Leads" },
      { key: "ctr", label: "CTR (%)" },
    ],
  },
  {
    label: "TikTok Ads",
    providers: ["tiktok"],
    color: "#010101",
    metrics: [
      { key: "impressions", label: "Impressions" },
      { key: "clicks", label: "Clicks" },
      { key: "spend", label: "Spend" },
      { key: "conversions", label: "Conversions" },
      { key: "video_views", label: "Video views" },
      { key: "ctr", label: "CTR (%)" },
    ],
  },
];

const DATE_PRESETS = [
  { label: "7d", relativeDays: 7 },
  { label: "30d", relativeDays: 30 },
  { label: "90d", relativeDays: 90 },
  { label: "Custom", relativeDays: null },
];

const CHART_COLORS = [
  "#5B5BD6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6",
  "#EC4899", "#14B8A6", "#F97316",
];

function fmt(value) {
  const n = Number(value || 0);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function fmtDate(value) {
  if (!value) return "—";
  try { return new Date(value).toLocaleDateString(); } catch { return String(value); }
}

function accountIcon(provider, kind) {
  if (provider === "meta" && kind === "instagram_business")
    return <InstagramIcon sx={{ color: "#E1306C", fontSize: 16 }} />;
  if (provider === "meta")
    return <FacebookIcon sx={{ color: "#1877F2", fontSize: 16 }} />;
  if (provider === "google")
    return <GoogleIcon sx={{ color: "#EA4335", fontSize: 16 }} />;
  return null;
}

// ── sub-components ────────────────────────────────────────────────────────────

function SideSection({ title, icon, children }) {
  return (
    <Paper sx={{ overflow: "hidden" }}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: "#FAFBFC",
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        {icon && React.cloneElement(icon, { sx: { fontSize: 16, color: "text.secondary" } })}
        <Typography variant="h5" sx={{ color: "text.primary", fontSize: "0.875rem" }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Paper>
  );
}

function AccountCheckbox({ account, checked, onChange }) {
  const kindTag =
    account.providerAccountKind === "instagram_business" ? "Instagram"
      : account.providerAccountKind === "facebook_page" ? "FB Page"
      : account.providerAccountKind === "facebook_ads_account" ? "Meta Ads"
      : account.providerAccountKind === "ga4_property" ? "GA4"
      : account.provider;

  return (
    <FormControlLabel
      control={
        <Checkbox
          size="small"
          checked={checked}
          onChange={(e) => onChange(account.id, e.target.checked)}
          sx={{ p: 0.5 }}
        />
      }
      label={
        <Stack direction="row" spacing={0.75} alignItems="center">
          {accountIcon(account.provider, account.providerAccountKind)}
          <Typography variant="body2" noWrap sx={{ maxWidth: 130, fontWeight: 500 }}>
            {account.displayName || account.providerAccountId}
          </Typography>
          <Chip
            size="small"
            label={kindTag}
            sx={{ height: 16, fontSize: "0.6rem", bgcolor: "#F1F5F9", color: "#64748B", border: "none" }}
          />
        </Stack>
      }
      sx={{ mr: 0, mb: 0.75, alignItems: "flex-start" }}
    />
  );
}

function SavedReportChip({ report, onLoad, onDelete }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        py: 1,
        px: 1.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "#FAFBFC",
        cursor: "pointer",
        transition: "all 0.15s",
        "&:hover": { bgcolor: "#F1F5F9", borderColor: "#CBD5E1" },
        mb: 0.75,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }} onClick={() => onLoad(report)}>
        <Typography variant="body2" noWrap sx={{ fontWeight: 600, color: "text.primary" }}>
          {report.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {(report.metrics || []).slice(0, 3).join(", ")}
          {(report.metrics || []).length > 3 ? ` +${(report.metrics || []).length - 3}` : ""} ·{" "}
          {report.relativeDays ? `Last ${report.relativeDays}d` : `${fmtDate(report.dateFrom)} – ${fmtDate(report.dateTo)}`}
        </Typography>
      </Box>
      <Stack direction="row" spacing={0.25}>
        <Tooltip title="Load" arrow>
          <IconButton size="small" onClick={() => onLoad(report)} sx={{ color: "primary.main", p: 0.5 }}>
            <FolderOpenOutlinedIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete" arrow>
          <IconButton size="small" color="error" onClick={() => onDelete(report.id)} sx={{ p: 0.5 }}>
            <DeleteOutlineIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
}

function MetricTotalCard({ metricKey, value }) {
  return (
    <Paper
      sx={{
        p: 2,
        transition: "box-shadow 0.15s",
        "&:hover": { boxShadow: "0 2px 8px rgba(15,23,42,0.08)" },
      }}
    >
      <Typography variant="overline" display="block" sx={{ mb: 0.5 }}>
        {metricKey}
      </Typography>
      <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, letterSpacing: "-0.02em", color: "text.primary" }}>
        {fmt(value)}
      </Typography>
    </Paper>
  );
}

// ── main view ─────────────────────────────────────────────────────────────────

export default function ReportsView() {
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [connected, setConnected] = useState([]);
  const [savedReports, setSavedReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState(null);
  const [result, setResult] = useState(null);
  const [reportName, setReportName] = useState("");

  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [datePreset, setDatePreset] = useState(30);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const isCustomDate = datePreset === null;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [me, connectedList, reports] = await Promise.all([
        ApiService.getAccountMe().catch(() => ({ account: null })),
        IntegrationsService.listConnected(),
        ReportService.listReports().catch(() => []),
      ]);
      setAccount(me?.account ?? null);
      setConnected(Array.isArray(connectedList) ? connectedList : []);
      setSavedReports(Array.isArray(reports) ? reports : []);
    } catch (e) {
      if (e?.response?.status === 401) { navigate("/signin"); return; }
      setError(e?.response?.data?.message || e.message || "Failed to load.");
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  function toggleAccount(id, checked) {
    setSelectedAccounts((prev) => checked ? [...prev, id] : prev.filter((x) => x !== id));
  }

  function toggleMetric(key, checked) {
    setSelectedMetrics((prev) => checked ? [...prev, key] : prev.filter((x) => x !== key));
  }

  async function handleRun() {
    if (selectedAccounts.length === 0) {
      setSnackbar({ severity: "warning", message: "Select at least one data source." });
      return;
    }
    if (selectedMetrics.length === 0) {
      setSnackbar({ severity: "warning", message: "Select at least one metric." });
      return;
    }
    setRunning(true);
    setError(null);
    try {
      const params = {
        connectedAccountIds: selectedAccounts,
        metrics: selectedMetrics,
        ...(isCustomDate ? { dateFrom, dateTo } : { relativeDays: datePreset }),
      };
      const data = await ReportService.runReport(params);
      setResult(data);
      if (!data.hasData) {
        setSnackbar({ severity: "info", message: "No data found. Sync your accounts first." });
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Report failed.");
    } finally { setRunning(false); }
  }

  async function handleSave() {
    if (!reportName.trim()) {
      setSnackbar({ severity: "warning", message: "Enter a report name first." });
      return;
    }
    setSaving(true);
    try {
      const params = {
        name: reportName.trim(),
        connectedAccountIds: selectedAccounts,
        metrics: selectedMetrics,
        ...(isCustomDate ? { dateFrom, dateTo } : { relativeDays: datePreset }),
      };
      await ReportService.saveReport(params);
      setSnackbar({ severity: "success", message: "Report saved." });
      await load();
    } catch (e) {
      setSnackbar({ severity: "error", message: e?.response?.data?.message || "Save failed." });
    } finally { setSaving(false); }
  }

  async function handleDeleteReport(id) {
    try {
      await ReportService.deleteReport(id);
      setSavedReports((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      setSnackbar({ severity: "error", message: "Delete failed." });
    }
  }

  function handleLoadReport(report) {
    setSelectedAccounts(report.connectedAccountIds || []);
    setSelectedMetrics(report.metrics || []);
    if (report.relativeDays) {
      setDatePreset(report.relativeDays);
    } else {
      setDatePreset(null);
      setDateFrom(report.dateFrom || "");
      setDateTo(report.dateTo || "");
    }
    setReportName(report.name || "");
    setResult(null);
  }

  async function handleSignOut() {
    try { await ApiService.signout(); } catch {}
    navigate("/signin");
  }

  const chartSeries = useMemo(() => {
    if (!result) return [];
    return selectedMetrics
      .filter((m) => result.series?.[m]?.length > 0)
      .map((m, i) => ({
        metricKey: m,
        points: result.series[m],
        color: CHART_COLORS[i % CHART_COLORS.length],
      }));
  }, [result, selectedMetrics]);

  const chartXDates = useMemo(() => {
    if (chartSeries.length === 0) return [];
    const all = new Set();
    chartSeries.forEach((s) => s.points.forEach((p) => all.add(p.date)));
    return [...all].sort().map((d) => new Date(d));
  }, [chartSeries]);

  const chartMuiSeries = useMemo(() => {
    return chartSeries.map((s) => {
      const dateMap = {};
      s.points.forEach((p) => { dateMap[p.date] = p.value; });
      const yData = chartXDates.map((d) => {
        const key = d.toISOString().slice(0, 10);
        return Number(dateMap[key] || 0);
      });
      return { data: yData, label: s.metricKey, color: s.color, area: false, curve: "monotoneX" };
    });
  }, [chartSeries, chartXDates]);

  const tableRows = useMemo(() => {
    if (!result) return [];
    const dateSet = new Set();
    selectedMetrics.forEach((m) => {
      (result.series?.[m] || []).forEach((p) => dateSet.add(p.date));
    });
    const dates = [...dateSet].sort();
    return dates.map((date) => {
      const row = { date };
      selectedMetrics.forEach((m) => {
        const point = (result.series?.[m] || []).find((p) => p.date === date);
        row[m] = point ? Number(point.value) : 0;
      });
      return row;
    });
  }, [result, selectedMetrics]);

  const hasNoConnections = !loading && connected.length === 0;

  const selectedAccountMeta = useMemo(() => {
    const ids = new Set(selectedAccounts);
    return connected
      .filter((c) => ids.has(c.id))
      .map((c) => ({ provider: c.provider, kind: c.providerAccountKind }));
  }, [selectedAccounts, connected]);

  const visibleGroups = useMemo(() => {
    if (selectedAccountMeta.length === 0) return METRIC_GROUPS;
    return METRIC_GROUPS.filter((g) =>
      selectedAccountMeta.some(({ provider, kind }) => {
        if (!g.providers.includes(provider)) return false;
        if (g.kinds && !g.kinds.includes(kind)) return false;
        if (g.excludeKinds && g.excludeKinds.includes(kind)) return false;
        return true;
      })
    );
  }, [selectedAccountMeta]);

  const canRun = selectedAccounts.length > 0 && selectedMetrics.length > 0;

  return (
    <AppShell account={account} onSignOut={handleSignOut} title="Report builder">
      <Stack spacing={3}>
        {/* Page header */}
        <Box>
          <Typography variant="h2">Report builder</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Pick data sources, choose metrics, set a date range, then run.
          </Typography>
        </Box>

        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

        {hasNoConnections ? (
          <Paper
            sx={{
              p: 8,
              textAlign: "center",
              background: "linear-gradient(135deg, #F8FAFC 0%, #EEF2FF 100%)",
              border: "1px dashed #C7D2FE",
            }}
          >
            <Box
              sx={{
                width: 64, height: 64, borderRadius: "16px", bgcolor: "#EEF2FF",
                display: "flex", alignItems: "center", justifyContent: "center",
                mx: "auto", mb: 2.5,
              }}
            >
              <HubOutlinedIcon sx={{ fontSize: 30, color: "#5B5BD6" }} />
            </Box>
            <Typography variant="h3" sx={{ mb: 1 }}>No data sources connected</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: "auto" }}>
              Connect Facebook, Instagram, or Google Analytics to start building custom reports.
            </Typography>
            <Button
              component={RouterLink}
              to="/integrations"
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{ mt: 3.5 }}
            >
              Connect data sources
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {/* ── Config panel ── */}
            <Grid item xs={12} md={4} lg={3}>
              <Stack spacing={2}>

                {/* Data sources */}
                <SideSection title="Data sources" icon={<HubOutlinedIcon />}>
                  {loading ? (
                    <Stack spacing={1}>
                      {[0, 1, 2].map((i) => <Skeleton key={i} height={28} />)}
                    </Stack>
                  ) : (
                    <FormGroup>
                      {connected.map((c) => (
                        <AccountCheckbox
                          key={c.id}
                          account={c}
                          checked={selectedAccounts.includes(c.id)}
                          onChange={toggleAccount}
                        />
                      ))}
                    </FormGroup>
                  )}
                </SideSection>

                {/* Metrics */}
                <SideSection title="Metrics" icon={<BarChartOutlinedIcon />}>
                  {visibleGroups.map((group) => (
                    <Box key={group.label} sx={{ mb: 2 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: group.color || "#5B5BD6",
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                            color: "text.secondary",
                          }}
                        >
                          {group.label}
                        </Typography>
                      </Stack>
                      <FormGroup>
                        {group.metrics.map((m) => (
                          <FormControlLabel
                            key={m.key}
                            control={
                              <Checkbox
                                size="small"
                                checked={selectedMetrics.includes(m.key)}
                                onChange={(e) => toggleMetric(m.key, e.target.checked)}
                                sx={{ p: 0.5 }}
                              />
                            }
                            label={
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {m.label}
                              </Typography>
                            }
                            sx={{ mb: 0.25 }}
                          />
                        ))}
                      </FormGroup>
                    </Box>
                  ))}
                </SideSection>

                {/* Date range */}
                <SideSection title="Date range" icon={<CalendarTodayOutlinedIcon />}>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1.5 }}>
                    {DATE_PRESETS.map((p) => {
                      const active = datePreset === p.relativeDays;
                      return (
                        <Box
                          key={p.label}
                          onClick={() => setDatePreset(p.relativeDays)}
                          sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1.5,
                            fontSize: "0.78rem",
                            fontWeight: 600,
                            cursor: "pointer",
                            mb: 0.5,
                            border: "1px solid",
                            transition: "all 0.15s",
                            ...(active
                              ? { bgcolor: "#5B5BD6", color: "#fff", borderColor: "#5B5BD6" }
                              : { bgcolor: "#F1F5F9", color: "#64748B", borderColor: "#E2E8F0", "&:hover": { borderColor: "#94A3B8" } }),
                          }}
                        >
                          {p.label}
                        </Box>
                      );
                    })}
                  </Stack>
                  {isCustomDate && (
                    <Stack spacing={1.5}>
                      <TextField
                        label="From"
                        type="date"
                        size="small"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                      <TextField
                        label="To"
                        type="date"
                        size="small"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                    </Stack>
                  )}
                </SideSection>

                {/* Run button */}
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  startIcon={running ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                  onClick={handleRun}
                  disabled={running || !canRun}
                  sx={{ py: 1.25 }}
                >
                  {running ? "Running…" : "Run report"}
                </Button>

                {!canRun && !running && (
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center", display: "block", mt: -1 }}>
                    Select a source and at least one metric
                  </Typography>
                )}

                {/* Save report */}
                <SideSection title="Save report" icon={<SaveOutlinedIcon />}>
                  <Stack spacing={1.5}>
                    <TextField
                      placeholder="Report name…"
                      size="small"
                      fullWidth
                      value={reportName}
                      onChange={(e) => setReportName(e.target.value)}
                    />
                    <Button
                      variant="outlined"
                      fullWidth
                      size="small"
                      startIcon={saving ? <CircularProgress size={13} /> : <SaveOutlinedIcon sx={{ fontSize: 15 }} />}
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? "Saving…" : "Save report"}
                    </Button>
                  </Stack>
                </SideSection>

                {/* Saved reports */}
                {savedReports.length > 0 && (
                  <SideSection title="Saved reports" icon={<FolderOpenOutlinedIcon />}>
                    {savedReports.map((r) => (
                      <SavedReportChip
                        key={r.id}
                        report={r}
                        onLoad={handleLoadReport}
                        onDelete={handleDeleteReport}
                      />
                    ))}
                  </SideSection>
                )}
              </Stack>
            </Grid>

            {/* ── Results panel ── */}
            <Grid item xs={12} md={8} lg={9}>
              {!result ? (
                <Paper
                  sx={{
                    height: "100%",
                    minHeight: 480,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "linear-gradient(135deg, #F8FAFC 0%, #EEF2FF 100%)",
                    border: "1px dashed #C7D2FE",
                  }}
                >
                  <Box sx={{ textAlign: "center", px: 4 }}>
                    <Box
                      sx={{
                        width: 64, height: 64, borderRadius: "16px", bgcolor: "#EEF2FF",
                        display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2,
                      }}
                    >
                      <TuneOutlinedIcon sx={{ fontSize: 28, color: "#5B5BD6" }} />
                    </Box>
                    <Typography variant="h3" sx={{ mb: 1 }}>Configure and run a report</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Select data sources, choose metrics, and pick a date range on the left — then hit Run.
                    </Typography>
                  </Box>
                </Paper>
              ) : (
                <Stack spacing={2.5}>
                  {/* Totals */}
                  <Grid container spacing={2}>
                    {selectedMetrics.map((m) => (
                      <Grid item xs={6} sm={4} md={3} key={m}>
                        <MetricTotalCard metricKey={m} value={result.totals?.[m]} />
                      </Grid>
                    ))}
                  </Grid>

                  {/* Chart */}
                  {chartXDates.length > 0 && chartMuiSeries.length > 0 && (
                    <Paper sx={{ overflow: "hidden" }}>
                      <Box
                        sx={{
                          px: 2.5,
                          py: 2,
                          borderBottom: "1px solid",
                          borderColor: "divider",
                          bgcolor: "#FAFBFC",
                        }}
                      >
                        <Typography variant="h4">Trend</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {result.dateFrom} – {result.dateTo} · {chartMuiSeries.length} metric{chartMuiSeries.length !== 1 ? "s" : ""}
                        </Typography>
                      </Box>
                      <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5 }}>
                        <LineChart
                          height={300}
                          xAxis={[{ data: chartXDates, scaleType: "time" }]}
                          series={chartMuiSeries}
                          margin={{ left: 56, right: 16, top: 16, bottom: 40 }}
                          sx={{
                            ".MuiLineElement-root": { strokeWidth: 2 },
                          }}
                        />
                      </Box>
                    </Paper>
                  )}

                  {/* Data table */}
                  <Card>
                    <Box
                      sx={{
                        px: 2.5,
                        py: 2,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        bgcolor: "#FAFBFC",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box>
                        <Typography variant="h4">Data table</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {tableRows.length} rows · {result.dateFrom} – {result.dateTo}
                        </Typography>
                      </Box>
                      <ExportButton
                        rows={tableRows}
                        filename={`report-${result.dateFrom}-${result.dateTo}.csv`}
                        columns={[
                          { label: "Date", value: (r) => r.date },
                          ...selectedMetrics.map((m) => ({ label: m, value: (r) => r[m] })),
                        ]}
                      />
                    </Box>

                    <Box sx={{ overflowX: "auto" }}>
                      {tableRows.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: "center" }}>
                          <Typography variant="body2" color="text.secondary">
                            No data. Sync your connected accounts first, then re-run.
                          </Typography>
                        </Box>
                      ) : (
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Date</TableCell>
                              {selectedMetrics.map((m) => (
                                <TableCell key={m} align="right">{m}</TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {tableRows.map((row) => (
                              <TableRow key={row.date} hover>
                                <TableCell sx={{ fontWeight: 500 }}>{row.date}</TableCell>
                                {selectedMetrics.map((m) => (
                                  <TableCell key={m} align="right">
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {fmt(row[m])}
                                    </Typography>
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </Box>
                  </Card>
                </Stack>
              )}
            </Grid>
          </Grid>
        )}
      </Stack>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} variant="filled" sx={{ borderRadius: 2 }}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </AppShell>
  );
}
